import { bridge } from "@multiweb/reactCore/bridge";
import { IRowData } from "@multiweb/types/contracts";
import { ActionType, TriggerType } from "./enums";
import { IAction, IParticipant, ITrigger } from "./types";
import { UiModelCreator } from "./UiModelCreator";
import { CONST_MENU_IDENTIFIER_AT } from "./workflowParser/constants";

const { generateUUID } = bridge;

/**
 * Vrací defaultní stav akce pro poskytnutý @param actionType.
 * @param actionType Typ akce vytvoření.
 */
export const getDefaultActionState = (actionType: ActionType): IAction => {
    switch (actionType) {
        case ActionType.CallMethod:
            return UiModelCreator.createCallMethodAction();
        case ActionType.CallScript:
            return UiModelCreator.createCallScriptAction();
        case ActionType.IncrementValue:
            return UiModelCreator.createIncrementNumberAction();
        case ActionType.SendMail:
            return UiModelCreator.createSendMailAction();
        case ActionType.SendSMS:
            return UiModelCreator.createSendSmsAction();
        case ActionType.SendWFMessage:
            return UiModelCreator.createSendWfMessageAction();
        case ActionType.SetAttribute:
            return UiModelCreator.createSetAttributeAction();
        case ActionType.SetObjectAttribute:
            return UiModelCreator.createSetObjectAttributeAction();
        case ActionType.SetValue:
            return UiModelCreator.createSetValueAction();
        case ActionType.OpenForm:
            return UiModelCreator.createOpenFormAction();
        default:
            throw new Error(`Default state of action type '${actionType}' is not implemented!`);
    }
};

/**
 * Vrací defaultní stav spoučtěče pro poskytnutý @param triggerType.
 * @param triggerType Type spouštěče.
 */
export const getDefaultTriggerState = (triggerType: TriggerType): ITrigger => {
    switch (triggerType) {
        case TriggerType.AttributeDataChange:
            return UiModelCreator.createAttributeDataChange();
        case TriggerType.Menu:
            return UiModelCreator.createMenu();
        case TriggerType.NewObject:
            return UiModelCreator.createNewObject();
        case TriggerType.WfMessage:
            return UiModelCreator.createWfMessage();
        default:
            throw new Error(`Default state of trigger type '${triggerType}' is not implemented!`);
    }
};

/**
 * Vytvoří kopii poskytnutého spouštěcě. (Prvky, které jsou unitkátní instanci, tak nekopíruje, ale vytvoří nové.)
 * @param triggerToCopy Spouštěč k okopírování.
 * @returns Okopírovaný spouštěč.
 */
export const createTriggerCopy = (triggerToCopy: ITrigger): ITrigger => {
    if (triggerToCopy.triggerType === TriggerType.Menu) {
        const newProperties = { ...triggerToCopy.properties };
        newProperties[CONST_MENU_IDENTIFIER_AT] = generateUUID();
        return { ...triggerToCopy, properties: newProperties };
    }

    return { ...triggerToCopy };
};

/**
 * Vytvoří pole hráčů typu Role z načtených objektů z DB.
 * @param roleDataRows Řádky z databáze pro hráče typu Role.
 */
export const createRoleParticipants = (roleDataRows: readonly IRowData[]) => {
    const roles: IParticipant[] = [];

    roleDataRows.forEach(roleDataRow => {
        const name = roleDataRow["Fyzicke_jmeno"].toString();
        const dbID = roleDataRow["ID"].toString();
        roles.push(UiModelCreator.createRoleParticipant(name, dbID));
    });

    return roles.sort((a, b) => (a.name.localeCompare(b.name)));
};

/**
 * Vytvoří pole hráčů typu User z načtených objektů z DB.
 * @param roleDataRows Řádky z databáze pro hráče typu User.
 */
export const createUserParticipants = (userDataRows: readonly IRowData[]) => {
    const users: IParticipant[] = [];

    userDataRows.forEach(userDataRow => {
        const name = userDataRow["Uzivatelske_jmeno"].toString();
        const dbID = userDataRow["ID"].toString();
        users.push(UiModelCreator.createUserParticipant(name, dbID));
    });

    return users.sort((a, b) => (a.name.localeCompare(b.name)));
};
